import {
  Box,
  Button,
  IconButton,
  Typography,
  LinearProgress,
  Alert,
  CircularProgress,
} from '@mui/material';
import Modal from '@mui/material/Modal';
import React from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { COLOR_RED, MONTHLY_MARKET_DASHBOARD_EXPORT_LIMIT } from 'src/utils/const';
import { parseMarketDataForPDFRequest } from './helper';
import { useSelector } from 'react-redux';
import { callApi } from 'src/core/callApi';
import { COLOR_TEXT_2 } from 'src/utils/colors';

const SAMPLE_PDF_URL =
  'https://airbtics-market-summary-pdfs.s3.amazonaws.com/market_summary_Gold%20Coast_20250910_090636___df1d43b5637ab5aef149b688107691d731355d3874df1b915d42fce72254643d.pdf';
const SAMPLE_PDF_IMAGE_URL =
  'https://airbtics-wordpress-market-assets.s3.us-east-2.amazonaws.com/demo_report_image-min.jpg';

const ExportMarketPdfModal = ({
  open,
  setOpen,
  currency,
  handleOnSampleClick,
  marketDetails,
  marketGradeInfo,
  marketDataCache,
  marketTrend,
  marketSeasonality,
  revenueFormattedChartData,
  formattedSubmarketSummary,
  top3SubmarketsRef,
  submarketSortKey,
  guestGraphData,
  isPdfDownloading,
  setIsPdfDownloading,
  handleExportDashboardPDF,
  topSubMarketChartRef,
  demandByAmenitiesChartRef,
  mainKpiRevenueChartRef,
  bestPropertyTypeGraphRef,
  listingByPropertySizeGraphRef,
  listingByPropertyTypeGraphRef,
  cancellationPolicyGraphRef,
  listingsByDaysAvailableGraphRef,
  listingsByCapacityGraphRef,
  domesticVsInternationalGraphRef,
  monthlyAlosDataGraphRef,
  monthlyBltDataGraphRef,
  occupancyRateCalendarDataHistorical,
  occupancyRateCalendarDataPrelim,
  occupancyRateCalendarDataFuture,
  dailyOccupancyRateCalendarHistoricalGraphRef,
  dailyOccupancyRateCalendarFutureGraphRef,
  filterVal,
  filterActive,
  isSubmarket,
  isFullHostUnlocked,
  isPDFDataToDownloadLoading,
  reportMarketHeader,
  configurableFilterBarCardSelectionModalRef,
}) => {
  const topHosts = useSelector(state => (state as any).dashboard.topHosts);
  const user = useSelector(state => (state as any).dashboard.user);

  const currentYearMonth = new Date().toISOString().slice(0, 7);
  const userSubscriptionPlan: 'starter' | 'expert' | 'scale' = user.subscriptionPlan;
  // const userSubscriptionPlan: 'starter' | 'expert' | 'scale' = 'starter';
  const userExportUsage = (user.marketDashboardExportUsage || {})[currentYearMonth] || 0;
  const userExportLimit = MONTHLY_MARKET_DASHBOARD_EXPORT_LIMIT[userSubscriptionPlan] || 0;
  const creditsRemaining = Math.max(userExportLimit - userExportUsage, 0);

  const handleExport = () => {
    if (creditsRemaining <= 0) {
      return;
    }

    const requestBody = parseMarketDataForPDFRequest(
      currency,
      marketDetails,
      marketGradeInfo,
      marketDataCache,
      marketTrend,
      marketSeasonality,
      revenueFormattedChartData,
      formattedSubmarketSummary,
      top3SubmarketsRef,
      submarketSortKey,
      guestGraphData,
      topHosts,
      topSubMarketChartRef,
      demandByAmenitiesChartRef,
      mainKpiRevenueChartRef,
      bestPropertyTypeGraphRef,
      listingByPropertySizeGraphRef,
      listingByPropertyTypeGraphRef,
      cancellationPolicyGraphRef,
      listingsByDaysAvailableGraphRef,
      listingsByCapacityGraphRef,
      domesticVsInternationalGraphRef,
      monthlyAlosDataGraphRef,
      monthlyBltDataGraphRef,
      occupancyRateCalendarDataHistorical,
      occupancyRateCalendarDataPrelim,
      occupancyRateCalendarDataFuture,
      dailyOccupancyRateCalendarHistoricalGraphRef,
      dailyOccupancyRateCalendarFutureGraphRef,
      filterVal,
      filterActive,
      isSubmarket,
      user,
      isFullHostUnlocked,
      reportMarketHeader,
      configurableFilterBarCardSelectionModalRef
    );

    handleExportDashboardPDF(requestBody);
  };

  const openSamplePdf = () => {
    window.open(SAMPLE_PDF_URL, '_blank');
  };

  const renderExpertScaleContent = () => {
    const isOutOfCredits = creditsRemaining <= 0;

    return (
      <>
        <Typography variant="h6" sx={{ textAlign: 'center', color: '#000' }}>
          Download {marketDetails.name} Market Summary PDF
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'center', mt: -1, color: COLOR_TEXT_2 }}>
          Generate and export a detailed PDF with the latest {marketDetails.name} market summary
          data.
        </Typography>

        <Typography variant="body1" sx={{ mt: 1, color: COLOR_TEXT_2 }}>
          Downloading this PDF will use <b>1 PDF export credit</b> from your monthly allowance.
        </Typography>

        <Box sx={{ width: '100%', mt: 1, px: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1" fontWeight={500}>
              Credits Remaining
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5, color: COLOR_TEXT_2, fontWeight: 500 }}>
              {creditsRemaining} / {userExportLimit}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(creditsRemaining / userExportLimit) * 100}
            sx={{ height: 8, borderRadius: 5, mt: 1 }}
            color={isOutOfCredits ? 'error' : 'primary'}
          />
          <Typography variant="caption" sx={{ color: COLOR_TEXT_2 }}>
            {userSubscriptionPlan.charAt(0).toUpperCase() + userSubscriptionPlan.slice(1)} Plan ·
            Resets monthly
          </Typography>
        </Box>

        {isOutOfCredits ? (
          (userSubscriptionPlan as any) === 'expert' ? (
            <Alert severity="warning" sx={{ width: '100%' }}>
              You’ve used all your monthly PDF export credits.{' '}
              <Button
                size="large"
                onClick={() => {
                  handleOnSampleClick('scale');
                  setOpen(false);
                }}
              >
                Upgrade to Scale
              </Button>{' '}
              for more credits.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ width: '100%' }}>
              You’ve used all your monthly PDF export credits.
              {userSubscriptionPlan === 'scale' && ' Please wait for it to reset next month.'}
            </Alert>
          )
        ) : null}

        <Button
          variant="contained"
          fullWidth
          disabled={isOutOfCredits}
          onClick={isPDFDataToDownloadLoading || isPdfDownloading ? () => {} : handleExport}
          sx={{ mt: 2, color: 'white' }}
        >
          {isPDFDataToDownloadLoading ? (
            <CircularProgress size={20} sx={{ color: 'white' }} />
          ) : isPdfDownloading ? (
            <CircularProgress size={20} sx={{ color: 'white' }} />
          ) : isOutOfCredits && (userSubscriptionPlan as any) === 'scale' ? (
            'No Credits Remaining'
          ) : (
            <>Download {marketDetails.name} Market Summary PDF</>
          )}
        </Button>

        <Typography variant="body2" sx={{ mt: 1, color: COLOR_TEXT_2, textAlign: 'center' }}>
          ** Use desktop for the best experience
        </Typography>
      </>
    );
  };

  const renderStarterContent = () => {
    return (
      <>
        <Typography variant="h6" sx={{ color: '#000', textAlign: 'center' }}>
          Download {marketDetails.name} Market Summary PDF
        </Typography>
        <Typography variant="body2" sx={{ color: COLOR_TEXT_2, textAlign: 'center' }}>
          Generate and export a detailed PDF with the latest {marketDetails.name} market summary
          data.
        </Typography>

        <Box
          onClick={openSamplePdf}
          sx={{ position: 'relative', width: '100%', cursor: 'pointer' }}
        >
          <img
            src={SAMPLE_PDF_IMAGE_URL}
            alt="Market Summary PDF Export Example PDF"
            style={{ width: '100%', height: 'auto' }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '60px',
              background: 'linear-gradient(to top, #fff, #fff0)',
            }}
          ></Box>
          <Box
            sx={{
              position: 'absolute',
              bottom: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              padding: '10px',
              borderRadius: '5px',
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#000',
              boxShadow: '0 3px 10px 0 rgba(0,0,0,0.25)',
            }}
          >
            Sample PDF
          </Box>
        </Box>

        <Alert severity="warning" sx={{ width: '100%', mt: 2 }}>
          Market summary PDF exports are available only on Expert and Scale plans.{' '}
          <Button
            size="small"
            onClick={() => {
              handleOnSampleClick('expert');
              setOpen(false);
            }}
          >
            Upgrade to Expert plan
          </Button>{' '}
          to unlock full PDF with detailed market analysis - or download the sample PDF to see
          what’s included.
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, mt: 2, width: '100%' }}>
          <Button fullWidth variant="outlined" onClick={openSamplePdf}>
            Download Sample
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              handleOnSampleClick('expert');
              setOpen(false);
            }}
          >
            Upgrade to Expert
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'clamp(320px, 100%, 520px)',
          bgcolor: 'white',
          borderRadius: '10px',
          p: 3,
          pt: 5,
          boxShadow: '0 0 10px 0 rgba(0,0,0,0.1)',
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <IconButton
          onClick={() => setOpen(false)}
          sx={{ position: 'absolute', top: 10, right: 10 }}
        >
          <CloseRoundedIcon sx={{ color: COLOR_RED }} />
        </IconButton>

        {(userSubscriptionPlan as any) === 'starter'
          ? renderStarterContent()
          : renderExpertScaleContent()}
      </Box>
    </Modal>
  );
};

export default ExportMarketPdfModal;
